import React, { useEffect, useState } from 'react';
import {
  FiVideo, FiTrendingUp, FiAward, FiArrowDownCircle, FiArrowUpCircle,
  FiShield, FiBriefcase, FiClock, FiCheckCircle, FiXCircle,
} from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import styles from './MyRequests.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';

interface ReqRow {
  id: string;
  req_type: 'meeting' | 'invest' | 'partner' | 'deposit' | 'withdrawal' | 'kyc' | 'kyb';
  amount: number | null;
  amount_inr: number | null;
  currency: string | null;
  status: string;
  created_at: string;
  detail: string | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  identity: 'Identity proof', address: 'Address proof', income: 'Income proof',
};

const TYPE_META: Record<string, { label: string; Icon: React.ElementType }> = {
  meeting: { label: 'Meeting request', Icon: FiVideo },
  invest: { label: 'Investment interest', Icon: FiTrendingUp },
  partner: { label: 'Partner application', Icon: FiAward },
  deposit: { label: 'Deposit', Icon: FiArrowDownCircle },
  withdrawal: { label: 'Withdrawal', Icon: FiArrowUpCircle },
  kyc: { label: 'KYC document', Icon: FiShield },
  kyb: { label: 'KYB verification', Icon: FiBriefcase },
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', confirmed: '#4fae7a', approved: '#4fae7a', paid: '#4fae7a',
  processing: '#1d60f6', rejected: '#e2534a',
};

const TYPE_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Meetings', value: 'meeting' },
  { label: 'Investment', value: 'invest' },
  { label: 'Partner', value: 'partner' },
  { label: 'Deposits', value: 'deposit' },
  { label: 'Withdrawals', value: 'withdrawal' },
  { label: 'KYC', value: 'kyc' },
  { label: 'KYB', value: 'kyb' },
];

const MyRequests: React.FC = () => {
  const { currencies: adminCurrencies } = useCurrency();
  const [requests, setRequests] = useState<ReqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    axiosClient.get('/leads/my-requests').then(({ data }) => setRequests(data.requests)).finally(() => setLoading(false));
  }, []);

  const filtered = typeFilter === 'all' ? requests : requests.filter((r) => r.req_type === typeFilter);

  return (
    <DashboardShell>
      <PageHeader
        title="My Requests"
        subtitle="Everything you've submitted — meetings, investments, Partner Program, deposits, withdrawals, and KYC/KYB — with live status."
        action={<WalletBadge />}
      />
      <div className={styles.page}>
        <select className={styles.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          {TYPE_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>

        {loading && <div className={styles.muted}>Loading…</div>}
        {!loading && filtered.length === 0 && <div className={styles.empty}>Nothing here yet.</div>}

        <div style={{ marginTop: 16 }}>
          {!loading && filtered.map((row) => {
            const meta = TYPE_META[row.req_type] || { label: row.req_type, Icon: FiShield };
            const color = STATUS_COLORS[row.status] || 'var(--muted)';
            return (
              <div key={`${row.req_type}-${row.id}`} className={styles.card}>
                <div className={styles.cardLeft}>
                  <div className={styles.iconBox}><meta.Icon /></div>
                  <div>
                    <b className={styles.name}>{meta.label}{row.req_type === 'kyc' && row.detail ? ` — ${CATEGORY_LABEL[row.detail] || row.detail}` : ''}</b>
                    <div className={styles.subline}>
                      {row.amount ? `${adminCurrencies.find((c) => c.code === (row.currency || 'INR'))?.symbol || (row.currency || 'INR') + ' '}${Number(row.amount).toLocaleString()} · ` : ''}{new Date(row.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <span className={styles.statusPill} style={{ color, borderColor: color }}>
                  {row.status === 'rejected' ? <FiXCircle /> : row.status === 'pending' || row.status === 'processing' ? <FiClock /> : <FiCheckCircle />}
                  {row.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardShell>
  );
};

export default MyRequests;
